import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle2, ArrowUpRight } from 'lucide-react';

const products = [
  {
    id: 'standard-pallet-racking',
    name: 'Pallet Racking Rental',
    description: 'Heavy-duty pallet racking systems on flexible rental models designed for fast-growing warehouses and 3PL operators.',
    image: 'https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/si0fgnpz_IMG_0309.png',
    features: ['High load capacity beams', 'VNA compatible layouts', 'Seismic compliant', 'Quick installation'],
    color: '#E63946',
  },
  {
    id: 'adjustable-pallet-racking',
    name: 'Adjustable Pallet Racking',
    description: 'Flexible racking with adjustable beam heights for dynamic inventory environments with changing storage needs.',
    image: 'https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/gda04au8_IMG_0310.png',
    features: ['Variable beam heights', 'Quick reconfiguration', 'High load capacity', 'Modular design'],
    color: '#E63946',
  },
  {
    id: 'drive-in-racking',
    name: 'Drive-In Pallet Racking',
    description: 'High-density storage for bulk products with LIFO inventory management. Maximum space utilization.',
    image: 'https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/yi6oc4bi_IMG_0311.png',
    features: ['Up to 85% space utilization', 'LIFO management', 'Cold storage compatible', 'High density'],
    color: '#E63946',
  },
  {
    id: 'shuttle-racking',
    name: 'High-Density Shuttle Systems',
    description: 'Rail-guided shuttle systems for deep-lane storage. Increase density without permanent infrastructure commitment.',
    image: 'https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/z96dsxp8_IMG_0312.png',
    features: ['Reduced aisle requirement', 'Higher pallet density', 'Optimized footprint', 'Scalable deployment'],
    color: '#E63946',
  },
  {
    id: 'asrs-racking',
    name: 'ASRS Based Racking',
    description: 'Fully automated storage and retrieval systems for 24/7 high-throughput, high-accuracy operations.',
    image: 'https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/z96dsxp8_IMG_0312.png',
    features: ['Full automation', '99.9%+ accuracy', '24/7 operation', 'WMS integration'],
    color: '#E63946',
  },
  {
    id: 'mezzanine-floors',
    name: 'Multi-Tier & Mezzanine',
    description: 'G+2 to G+4 modular multi-tier shelving systems. Maximize vertical cube without civil modification.',
    image: 'https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/tqm4hqmx_IMG_0313.png',
    features: ['No civil modification', 'Bolt-based assembly', 'Fast dismantling', 'G+2 to G+4 levels'],
    color: '#E63946',
  },
];

export default function Products() {
  return (
    <div className="bg-[#0a1628] pt-20" data-testid="products-page">
      {/* Hero Section */}
      <section className="py-28 md:py-36 relative overflow-hidden">
        <div className="absolute inset-0 tech-grid-bg opacity-30" />
        <div className="absolute top-1/3 right-1/4 w-[600px] h-[600px] bg-[#E63946] opacity-[0.03] blur-[150px] rounded-full" />
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <span className="font-mono text-xs text-[#E63946] uppercase tracking-[0.2em] mb-6 block">
              // Our Products
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase leading-[0.9] tracking-tight mb-8">
              Storage Solutions <span className="gradient-text-gold">Built to Scale</span>
            </h1>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed max-w-3xl">
              From standard pallet racking to fully automated ASRS systems, our comprehensive 
              product range covers every warehousing need. All available on flexible rental terms.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 md:py-28 bg-[#080808]" data-testid="products-grid">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <Link
                key={product.id}
                to={`/products/${product.id}`}
                className="group"
                data-testid={`product-card-${product.id}`}
              >
                <div className="product-card h-full">
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden image-shine">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                    
                    {/* Number Badge */}
                    <div className="absolute top-4 left-4">
                      <div 
                        className="font-mono text-xs px-3 py-1.5 bg-black/60 backdrop-blur-sm border"
                        style={{ borderColor: `${product.color}50` }}
                      >
                        <span style={{ color: product.color }}>0{index + 1}</span>
                      </div>
                    </div>
                    
                    {/* Arrow */}
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-10 h-10 flex items-center justify-center bg-white/10 backdrop-blur-sm">
                        <ArrowUpRight className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="font-heading text-xl font-bold text-white uppercase mb-3 group-hover:text-[#FFD700] transition-colors duration-300">
                      {product.name}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-5">
                      {product.description}
                    </p>

                    {/* Features */}
                    <div className="space-y-2.5 mb-6">
                      {product.features.slice(0, 2).map((feature, fIndex) => (
                        <div key={fIndex} className="flex items-center gap-3 text-gray-400 text-sm">
                          <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: product.color }} />
                          {feature}
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center text-sm font-medium group-hover:text-[#FFD700] transition-colors" style={{ color: product.color }}>
                      Learn More 
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-2 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 md:py-36">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="font-mono text-xs text-[#FFD700] uppercase tracking-[0.2em] mb-6 block">
                // Custom Solutions
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
                Need a <span className="gradient-text-blue">Tailored Solution?</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-10">
                Every warehouse is unique. Our team can design custom storage solutions that 
                perfectly fit your space, inventory profile, and operational requirements.
              </p>
              <Link to="/get-quote" data-testid="products-get-quote">
                <Button className="btn-gold magnetic-btn clip-slant h-14 px-10 text-base">
                  Get a Custom Quote <ArrowRight className="ml-3 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="industrial-card bg-[#0a0a0a] border border-white/5 p-8 lg:p-10">
              <h3 className="font-heading text-xl font-bold text-white uppercase mb-8">All Products Include:</h3>
              <div className="space-y-5">
                {[
                  { text: 'Flexible rental terms', desc: 'Scale up or down as needed' },
                  { text: 'Professional installation', desc: 'Certified installation teams' },
                  { text: 'Quality certifications', desc: 'ISO certified products' },
                  { text: 'Preventive maintenance', desc: 'Regular upkeep included' },
                  { text: 'Technical support', desc: '24/7 support available' },
                  { text: 'Scalability options', desc: 'Grow with your business' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-3 bg-white/5 border border-white/5 hover:border-[#3B82F6]/30 transition-colors group">
                    <CheckCircle2 className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white font-medium text-sm group-hover:text-[#FFD700] transition-colors">{item.text}</p>
                      <p className="text-gray-600 text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
